import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	useJiraProjects,
	useMembers,
	useSetApprovedJiraProjects,
} from "@/hooks";
import {
	type ApprovalRow,
	type SavePlan,
	approvalRows,
	approvalsChanged,
	approvedIds,
	approvedProjectsErrorMessage,
	goneProjects,
	savePlan,
} from "@/lib/jira";
import type { JiraSiteProject, OrgMember } from "@/types/api";
import { useState } from "react";

/** Past this many projects the list gets a filter box. */
const FILTER_FROM = 8;

/**
 * Which of the site's Jira projects personalize their assignees' learning plans. Only organization admins
 * see it: the Gateway lists and sets projects for admins only.
 */
export function JiraApprovedProjects() {
	const projects = useJiraProjects();
	const members = useMembers();
	const save = useSetApprovedJiraProjects();
	// The admin's unsaved ticks; null while they match what is approved, so a refetch shows through.
	const [edits, setEdits] = useState<Set<string> | null>(null);
	const [filter, setFilter] = useState("");
	const [confirming, setConfirming] = useState<Extract<
		SavePlan,
		{ kind: "confirm" }
	> | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);
	const data = projects.data;
	const ticked = edits ?? approvedIds(data);

	function toggle(id: string) {
		setSaved(false);
		setError(null);
		const next = new Set(ticked);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		setEdits(next);
	}

	function requestSave() {
		if (!data) return;
		const plan = savePlan(data, ticked);
		if (plan.kind === "confirm") setConfirming(plan);
		else if (plan.kind === "save") void commit(plan.list);
	}

	/** Saves exactly `list`: for a confirmed save, the list the dialog showed. */
	async function commit(list: JiraSiteProject[]) {
		setError(null);
		setSaved(false);
		try {
			await save.mutateAsync(list);
			setEdits(null);
			setSaved(true);
		} catch (e) {
			setError(approvedProjectsErrorMessage(e));
		} finally {
			setConfirming(null);
		}
	}

	return (
		<div className="flex flex-col gap-3 rounded-md border border-divider p-3">
			<div>
				<p className="text-caption font-medium text-heading">
					Approved Jira projects
				</p>
				<p className="text-caption text-muted-foreground">
					People assigned tickets in an approved project get learning plans
					personalized from their assigned work.
				</p>
			</div>

			{projects.isPending ? (
				<Skeleton className="h-24 w-full" />
			) : projects.isError ? (
				<div className="flex flex-wrap items-center gap-3">
					<p className="text-caption text-error">
						{approvedProjectsErrorMessage(projects.error)}
					</p>
					<Button variant="outline" onClick={() => projects.refetch()}>
						Retry
					</Button>
				</div>
			) : projects.data.projects.length === 0 ? (
				<p className="text-caption text-muted-foreground">
					Your Jira site has no projects Curo can see yet.
				</p>
			) : (
				<>
					{projects.data.projects.length > FILTER_FROM ? (
						<Input
							value={filter}
							onChange={(e) => setFilter(e.target.value)}
							placeholder="Filter projects"
							aria-label="Filter projects"
						/>
					) : null}
					<ProjectList
						rows={filterRows(approvalRows(projects.data), filter)}
						ticked={ticked}
						onToggle={toggle}
						disabled={save.isPending}
						approverName={(userId) =>
							approverName(members.data?.members, userId)
						}
					/>
					<GoneProjects
						names={goneProjects(projects.data).map((p) => p.name)}
					/>
					<div className="flex flex-wrap items-center gap-3">
						<Button
							onClick={requestSave}
							disabled={
								save.isPending || !approvalsChanged(projects.data, ticked)
							}
						>
							{save.isPending ? "Saving…" : "Save approved projects"}
						</Button>
						{saved ? (
							<p role="status" className="text-caption text-muted-foreground">
								Saved.
							</p>
						) : null}
					</div>
				</>
			)}

			{error ? (
				<p role="alert" className="text-caption text-error">
					{error}
				</p>
			) : null}

			<AlertDialog
				open={confirming !== null}
				onOpenChange={(open) => {
					if (!open && !save.isPending) setConfirming(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Approve {projectCount(confirming?.fresh)}?
						</AlertDialogTitle>
						<AlertDialogDescription asChild>
							<div>
								<ApprovalNotice projects={confirming?.fresh ?? []} />
							</div>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={save.isPending}>
							Cancel
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={save.isPending}
							onClick={(e) => {
								// Stay open while saving; the outcome then shows in the card.
								e.preventDefault();
								if (confirming) void commit(confirming.list);
							}}
						>
							{save.isPending ? "Approving…" : "Approve"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

/** What approving means for the people in these projects, said before the admin confirms. */
export function ApprovalNotice({ projects }: { projects: JiraSiteProject[] }) {
	return (
		<div className="flex flex-col gap-2 text-body text-muted-foreground">
			<ul className="list-disc pl-5 text-heading">
				{projects.map((p) => (
					<li key={p.id}>
						{p.name} ({p.key})
					</li>
				))}
			</ul>
			<p>
				Assignees of tickets in{" "}
				{projects.length === 1 ? "this project" : "these projects"} will have
				their Curo learning plans personalized from their assigned work.
			</p>
			<p>
				People are matched by their Atlassian email, which must be visible to
				"Anyone" in their Atlassian profile.
			</p>
		</div>
	);
}

export function ProjectList({
	rows,
	ticked,
	onToggle,
	disabled,
	approverName,
}: {
	rows: ApprovalRow[];
	ticked: ReadonlySet<string>;
	onToggle: (id: string) => void;
	disabled: boolean;
	approverName: (userId: string) => string;
}) {
	if (rows.length === 0)
		return (
			<p className="text-caption text-muted-foreground">
				No projects match that filter.
			</p>
		);
	return (
		<ul className="flex max-h-80 flex-col divide-y divide-divider overflow-y-auto rounded-md border border-divider">
			{rows.map((row) => (
				<li key={row.id}>
					<label className="flex cursor-pointer items-start gap-3 px-3 py-2">
						<input
							type="checkbox"
							className="mt-0.5 h-4 w-4 accent-primary"
							checked={ticked.has(row.id)}
							disabled={disabled}
							onChange={() => onToggle(row.id)}
						/>
						<span className="flex flex-col">
							<span className="text-caption text-heading">
								{row.name} ({row.key})
							</span>
							{row.approval ? (
								<span className="text-caption text-muted-foreground">
									Approved by {approverName(row.approval.approvedBy)} on{" "}
									{new Date(row.approval.approvedAt).toLocaleDateString()}
								</span>
							) : null}
						</span>
					</label>
				</li>
			))}
		</ul>
	);
}

function GoneProjects({ names }: { names: string[] }) {
	if (names.length === 0) return null;
	return (
		<p className="text-caption text-muted-foreground">
			No longer on your Jira site: {names.join(", ")}. Saving removes{" "}
			{names.length === 1 ? "its approval" : "their approvals"}.
		</p>
	);
}

function filterRows(rows: ApprovalRow[], filter: string): ApprovalRow[] {
	const q = filter.trim().toLowerCase();
	if (!q) return rows;
	return rows.filter(
		(r) => r.name.toLowerCase().includes(q) || r.key.toLowerCase().includes(q),
	);
}

/** The approver's name, falling back to their email, then to "an admin" for someone no longer listed. */
export function approverName(
	members: OrgMember[] | undefined,
	userId: string,
): string {
	const member = members?.find((m) => m.user_id === userId);
	return member?.user_name || member?.email || "an admin";
}

function projectCount(projects: JiraSiteProject[] | undefined): string {
	const n = projects?.length ?? 0;
	return n === 1 ? "1 project" : `${n} projects`;
}
